"use client";

import { Table } from "@tanstack/react-table";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { MultiValueTag } from "@/components/ui/MultiValueTag";
import {
  DateRangePicker,
  DateRangePickerRef,
} from "@/pages/tax/access-request/components/date-range-picker";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { getSelectClasses } from "@/utils/filter-styles";
import { formatOptionLabel } from "@/utils/format-option-label";
import { SelectInput } from "components/Select";
import React from "react";
import { statusOptions } from "../data/options";
import { useToolbarFilters } from "../filter/hooks/use-toolbar-filters";
import { DataTableViewOptions } from "./data-table-view-options";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const dateRangePickerRef = React.useRef<DateRangePickerRef>(null);
  const { data: storeOptions = [] } = useGetStoresOptionsQuery();

  const {
    searchValue,
    storeValue,
    statusValue,
    dateRange,
    handleInputChange,
    handleStoreChange,
    handleStatusChange,
    handleDateRangeChange,
    clearAllFilters,
  } = useToolbarFilters(table);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between w-full">
      <div className="flex flex-1 flex-wrap items-end gap-2">
        <div className="relative">
          <Input
            value={searchValue}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              const isNumeric = /^[0-9]$/.test(e.key);
              const isControl = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Enter",
                "Home",
                "End",
              ].includes(e.key);

              if (!isNumeric && !isControl) {
                e.preventDefault();
              }
            }}
            className="h-8 w-[200px] pt-2"
            placeholder=" "
            pattern="[0-9]*"
            inputMode="numeric"
          />
          <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-medium text-gray-700">
            Filtrar por CPF
          </label>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="store_code"
            className="text-sm font-medium text-gray-700"
          >
            Loja
          </label>
          <SelectInput
            options={storeOptions || []}
            placeholder="Selecionar loja"
            value={storeValue}
            onChange={handleStoreChange}
            className={`${getSelectClasses(storeValue.length > 0)} h-8 min-w-[180px]`}
            classNamePrefix="an-simple-select"
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ MultiValue: MultiValueTag }}
            formatOptionLabel={formatOptionLabel}
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="status" className="text-sm font-medium text-gray-700">
            Status
          </label>
          <Select value={statusValue} onValueChange={handleStatusChange}>
            <SelectTrigger id="status" className="h-8 w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="date_range"
            className="text-sm font-medium text-gray-700"
          >
            Período
          </label>
          <DateRangePicker
            ref={dateRangePickerRef}
            onDateChange={handleDateRangeChange}
            initialDateRange={dateRange}
          />
        </div>

        {isFiltered && (
          <Button
            variant="ghost_disabled"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2"
          >
            Resetar
            <X className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            clearAllFilters(() => dateRangePickerRef.current?.clear())
          }
          className="h-8 px-2 text-xs text-muted-foreground"
        >
          <X className="mr-1 h-3 w-3" />
          Limpar filtros
        </Button>
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
